import { CSSObject } from 'styled-components';
import Select, { ValueContainerProps, components } from 'react-select';
import { FC, useMemo, useRef, useState } from 'react';
import IconCross from '@shared/icons/IconCross';

interface IStyleObject {
  [key: string]: any;
}

const SelectStyles: IStyleObject = {
  control: (provided: any, state: any) => {
    return {
      ...provided,
      backgroundColor: '#211F24',
      minHeight: state.selectProps.isFilter ? '42px' : '30px',
      borderColor: 'none',
      border: 'none',
      color: 'white',
      boxShadow: 'none',
      width: '100%',
      cursor: 'pointer',
    };
  },
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    padding: 5,
  }),
  clearIndicator(base: CSSObject): CSSObject {
    base.padding = 0;
    base.display = 'none';
    // иконка отмены всех опшенов
    return base;
  },
  indicatorsContainer(base: CSSObject): CSSObject {
    base.padding = 10;
    base.display = 'none';
    // область открытия дропдауна

    return base;
  },
  placeholder: (base: CSSObject): CSSObject => {
    return {
      ...base,
      color: '#ffffff',
      fontSize: '12px',
    };
  },
  noOptionsMessage: (base: CSSObject): CSSObject => {
    return {
      ...base,
      backgroundColor: '#252328',
      color: 'white',
    };
  },
  menu: (base: CSSObject): CSSObject => {
    return {
      ...base,
      backgroundColor: '#252328',
      color: 'white',
      border: '1px solid #211F24',
      borderRadius: '10px',
      overflow: 'hidden',
      width: '200px',
      zIndex: '30',
    };
  },
  indicatorSeparator(base: CSSObject): CSSObject {
    base.padding = 0;
    base.display = 'none';
    // vertical line between icons in input

    return base;
  },
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: '#252328',
    color: 'white',
    width: '200px',
  }),
  input: (provided: any, state: any) => ({
    ...provided,
    input: {
      margin: '0px',
    },
    height: '29px',
    fontSize: '12px',
    paddingTop: '7px',
    width: '100%',
    color: 'white',
    paddingBottom: '7px',
    marginTop: 0,
    marginBottom: 0,
  }),
};

const formatOptionLabel = (item: any, context: any) => {
  const selected = context.selectValue.map((option: any) => option.value);

  return (
    <div className="flex items-center gap-[10px] cursor-pointer z-30">
      <div
        className={`rounded-[50%] w-[14px] min-w-[14px] h-[14px] border border-solid border-[white] ${selected.includes(item.value) ? 'bg-[white]' : 'red'}`}
      />
      <div>{item.label}</div>
    </div>
  );
};

interface ICustomSelectProps {
  options: any;
  value: ISelectOption[];
  title: string;
  isLoading: boolean;
  width?: number;
  isMulti?: boolean;
  showSelections?: boolean;
  onChange?: (values: ISelectOption[]) => void;
}

export interface ISelectOption {
  value: string;
  label: string;
}

export type Option = {
  value: string;
  label: string;
};

const ValueContainer = ({ children, ...props }: ValueContainerProps<Option, boolean>) => {
  let [values, input] = children as any;
  const [focused, setFocused] = useState(false);

  if (Array.isArray(values)) {
    if (values.length > 1) {
      values = `${values.length} элемент(а)`;
    } else {
      values = values[0].props.data.label;
    }
  }

  return (
    <components.ValueContainer {...props}>
      <div className="w-full flex">
        {!focused ? (
          <div className="overflow-hidden text-ellipsis whitespace-nowrap pt-[5px]">{values}</div>
        ) : null}
        <div onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
          {input}
        </div>
      </div>
    </components.ValueContainer>
  );
};

const ShowSelectionsValueContainer = ({
  children,
  ...props
}: ValueContainerProps<Option, boolean>) => {
  let [values, input] = children as any;
  const [focused, setFocused] = useState(false);

  if (Array.isArray(values)) {
    values = values.map(value => value.props.data.label).join(', ');
  }

  return (
    <components.ValueContainer {...props}>
      <div className="w-full flex">
        {!focused ? (
          <div className="overflow-hidden text-ellipsis whitespace-nowrap pt-[5px] pr-[15px]">
            {values}
          </div>
        ) : null}
        <div onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
          {input}
        </div>
      </div>
    </components.ValueContainer>
  );
};

const MultiSelect: FC<ICustomSelectProps> = ({
  value,
  options,
  title,
  onChange,
  isLoading = false,
  width = 150,
  isMulti = true,
  showSelections = false,
}) => {
  const selectRef = useRef(null);

  return (
    <div
      style={{ minWidth: `${width}px`, maxWidth: `${width}px` }}
      className={`min-w-[${width}px] rounded-[8px] px-[5px] bg-[#211F24] text-[black] cursor-pointer relative text-[12px] h-[33px]`}
    >
      <Select
        ref={selectRef}
        options={options}
        isMulti={isMulti}
        value={value.map(val => ({ ...val, value: `${val.value}` }))}
        className="h-[33px]"
        openMenuOnFocus={true}
        //controlShouldRenderValue={false}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        styles={SelectStyles}
        isClearable={true}
        isLoading={isLoading}
        placeholder={title}
        formatOptionLabel={formatOptionLabel}
        onInputChange={(val: string, actionMeta: any) => {
          const items = (val || '')
            .split(/[.,\s]+/)
            .map(item => item.trim())
            .filter(Boolean);

          if (2 > items.length) {
            return;
          }

          if ('input-change' === actionMeta.action) {
            let selected = options.filter((item: ISelectOption) => items.includes(item.value));

            if (selected.length) {
              onChange(selected);
            }
          }
        }}
        onChange={(options: any) => {
          if (onChange) {
            onChange(options);
          }
        }}
        components={{
          ValueContainer: showSelections ? ShowSelectionsValueContainer : ValueContainer,
        }}
      />
      {/* @ts-ignore */}
      <svg
        onClick={() => selectRef.current.focus()}
        className="absolute right-[10px] top-[14px]"
        width="7"
        height="5"
        viewBox="0 0 7 5"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.71098 1.98938L4.18608 4.24524C3.80588 4.58492 3.19172 4.58492 2.81152 4.24524L0.286622 1.98939C-0.327542 1.44066 0.111146 0.500001 0.978776 0.500001L6.02857 0.5C6.8962 0.5 7.32514 1.44066 6.71098 1.98938Z"
          fill="white"
        />
      </svg>

      {!value.length ? (
        <>
          {/* @ts-ignore */}
          <svg
            onClick={() => selectRef.current.focus()}
            className="absolute right-[10px] top-[14px]"
            width="7"
            height="5"
            viewBox="0 0 7 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.71098 1.98938L4.18608 4.24524C3.80588 4.58492 3.19172 4.58492 2.81152 4.24524L0.286622 1.98939C-0.327542 1.44066 0.111146 0.500001 0.978776 0.500001L6.02857 0.5C6.8962 0.5 7.32514 1.44066 6.71098 1.98938Z"
              fill="white"
            />
          </svg>
        </>
      ) : (
        <div
          className="absolute right-[5px] top-[6px] bg-[#211F24] py-[5px] pl-[5px]"
          onClick={() => {
            if (onChange) {
              onChange([]);
            }
          }}
        >
          <IconCross width={11} height={11} />
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
